// Where a person is, in presence terms, and back: the shell's route ↔ ProjectPresence.where, and the
// keys of tree nodes and cards ↔ where. System folders (Episodes, Elements, Canvases, Assets) are sent
// as kind "folder" with a non-uuid id ("elements", "elements:character"), so following someone there
// lands on the same folder. Pure.
import type { PresenceWhere } from "@filmsquare/realtime/presence";
import type { Loc, Route } from "../route.ts";
import type { ElementSubtype } from "../types.ts";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SUBTYPES = ["character", "location", "prop", "style"];

/** The project a route is in, or null (home, a canvas outside a project, an old address). */
export function projectOfRoute(r: Route): string | null {
  if (r.view === "project" || r.view === "shot" || r.view === "element") return r.projectId;
  if (r.view === "canvas") return r.projectId;
  return null;
}

function whereOfLoc(projectId: string, loc: Loc): PresenceWhere {
  switch (loc.kind) {
    case "root":
      return { kind: "project", id: projectId };
    case "episode":
      return { kind: "episode", id: loc.id };
    case "scene":
      return { kind: "scene", id: loc.id };
    case "cut":
    case "storyboard":
    case "tools":
    case "review":
    case "blocking":
    case "tags":
      return { kind: "episode", id: loc.episodeId };
    case "folder":
      return { kind: "folder", id: loc.id };
    case "elements":
      return { kind: "folder", id: loc.subtype ? `elements:${loc.subtype}` : "elements" };
    default:
      return { kind: "folder", id: loc.kind };
  }
}

/** Where this route is, without a label (the page adds one). */
export function whereOfRoute(r: Route): PresenceWhere | null {
  switch (r.view) {
    case "project":
      return whereOfLoc(r.projectId, r.loc);
    case "shot":
      return { kind: "shot", id: r.shotId };
    case "element":
      return { kind: "element", id: r.elementId };
    case "canvas":
      return r.projectId ? { kind: "canvas", id: r.id } : null;
    default:
      return null;
  }
}

/** Looks up what a bare id cannot tell: the episode of a scene. */
export interface WhereResolver {
  episodeOfScene?: (sceneId: string) => string | undefined;
}

/** The route to go to when following someone who is at `w` in `projectId`. Null: nowhere we can go. */
export function routeOfWhere(projectId: string, w: PresenceWhere, resolve: WhereResolver = {}): Route | null {
  const project = (loc: Loc): Route => ({ view: "project", projectId, loc });
  const id = w.id.toLowerCase();
  switch (w.kind) {
    case "project":
      return project({ kind: "root" });
    case "episode":
      return UUID.test(id) ? project({ kind: "episode", id }) : null;
    case "scene": {
      const ep = resolve.episodeOfScene?.(id);
      return ep ? project({ kind: "scene", episodeId: ep, id }) : null;
    }
    case "shot":
      return UUID.test(id) ? { view: "shot", projectId, shotId: id } : null;
    case "element":
      return UUID.test(id) ? { view: "element", projectId, elementId: id } : null;
    case "canvas":
      return UUID.test(id) ? { view: "canvas", projectId, id } : null;
    case "folder": {
      if (UUID.test(id)) return project({ kind: "folder", id });
      if (id === "episodes" || id === "canvases" || id === "assets") return project({ kind: id });
      if (id === "elements") return project({ kind: "elements" });
      const [a, b] = id.split(":");
      if (a === "elements" && b && SUBTYPES.includes(b))
        return project({ kind: "elements", subtype: b as ElementSubtype });
      return null;
    }
    default:
      return null;
  }
}

/**
 * The presence place a tree node or card stands for (`shot:<id>`, `episode:<id>`, `folder:<id>`,
 * `scene:<id>`, `element:<id>`, `canvas:<id>`, and the system folders `sys:elements:prop`). Null when
 * nobody can "be" there.
 */
export function whereOfKey(key: string): Pick<PresenceWhere, "kind" | "id"> | null {
  const i = key.indexOf(":");
  const kind = i < 0 ? key : key.slice(0, i);
  const id = i < 0 ? "" : key.slice(i + 1);
  switch (kind) {
    case "shot":
    case "episode":
    case "scene":
    case "folder":
    case "element":
    case "canvas":
      return id ? { kind, id } : null;
    case "sys":
      return id ? { kind: "folder", id } : null;
    case "episodes":
    case "canvases":
    case "assets":
      return { kind: "folder", id: kind };
    case "elements":
      return { kind: "folder", id: id ? `elements:${id}` : "elements" };
    default:
      return null;
  }
}
